import { useRef, useState } from 'react'
import { Button } from '@mantine/core'
import { IoImagesOutline } from 'react-icons/io5'
import SerialBlurModal from '../../app/common/imageUpload/SerialBlurModal'
import { ensureUploadableImageFile } from '../../app/common/imageUpload/ensureUploadableImageFile'
import agent from '../../app/api/agent'
import type { ContentPhotoDto } from '../../app/models/contentPhoto'
import { ContentPhotoOwnerType } from '../../app/models/contentPhoto'
import { toast } from 'react-toastify'

type Props = {
  ownerType: ContentPhotoOwnerType;
  ownerId: string;
  canUpload: boolean;
  onSuccess: (photo: ContentPhotoDto) => void;
  label?: string;
};

/** Uploads a gallery image via agent.ContentPhotos (serial blur → Cloudinary). */
export default function GalleryUploadButton ({
  ownerType,
  ownerId,
  canUpload,
  onSuccess,
  label = 'Add photos'
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  if (!canUpload) return null

  const handlePick = () => inputRef.current?.click()

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (f) setPendingFile(f)
  }

  const doUpload = async (file: File) => {
    let normalized: File
    try {
      normalized = await ensureUploadableImageFile(file)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not prepare image.')
      return
    }
    setUploading(true)
    try {
      const photo = (await agent.ContentPhotos.upload(ownerType, ownerId, normalized)) as ContentPhotoDto
      onSuccess(photo)
      toast.success('Photo added.')
    } catch {
      /* axios interceptor */
    } finally {
      setUploading(false)
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
      <Button
        type="button"
        variant="light"
        size="xs"
        leftIcon={<IoImagesOutline size={16} />}
        loading={uploading}
        onClick={handlePick}
        sx={{ fontFamily: 'DM Sans, sans-serif' }}
      >
        {label}
      </Button>
      <SerialBlurModal
        file={pendingFile}
        onClose={() => setPendingFile(null)}
        onConfirm={(file) => {
          setPendingFile(null)
          void doUpload(file)
        }}
        onSkip={(file) => {
          setPendingFile(null)
          void doUpload(file)
        }}
      />
    </>
  )
}
